import {
	PAGES,
	WORDS,
	CANONICAL_HOST,
	CANONICAL_PROTOCOL,
} from "@/common/consts";
import { sanitizeTiles } from "@/common/functions/tileUtils";
import { isEmpty } from "@/common/functions/objectUtils";

/**
 * Create a canonical URL from Vue router's current route
 * @param {String} route - The Vue Router's current route object
 * @return {String} The canonical URL with fixed protocol and no trailing slash
 */
export const canonical = (route, includeQuery = false) => {
	let isEnyePath = false;

	// Lowercase route (except in enye-related paths)
	const map = Array.prototype.map;
	let path = map
		.call(route.path, (letter) => {
			if (isEnyePath && letter === "N") return letter;
			return letter.toLowerCase();
		})
		.join("");

	// Fix missing leading slash in relative path
	path = path.charAt(0) !== "/" ? `/${path}` : path;

	// Sanitize and alphabetize tiles for results
	path = /\/([^\/]+)\/([^\/]+)\/?$/.test(path)
		? path.replace(/\/([^\/]+)\/([^\/]+)\/?$/, (match, p1, p2) => {
				if (["unscramble", "scrabble-dictionary"].includes(p1)) {
					return `/${p1}/` + sanitizeTiles(p2, route.name);
				} else {
					return `/${p1}/${p2}`;
				}
		  })
		: path;

	let url = `${CANONICAL_PROTOCOL}//${CANONICAL_HOST}${path}`;
	url = `${url.replace(/\/$/g, "")}/`;

	const pageParam = route.query?.page;
	url = includeQuery && pageParam ? `${url}?page=${pageParam}` : url;

	return {
		rel: "canonical",
		href: url,
	};
};

/**
 * Return the base URL for the corresponding routeName
 * @param {String} routeName - A $route.name string
 * @return {String} the pase relatie URL for the results page
 *
 *
 * @todo @idea @refactor doesn't it make more sense to have this as a getter?
 */
export const resultsPageURL = () => {
	if (
		store.getters["navigationChecks/isScrabblePage"] ||
		store.getters["navigationChecks/isScrabbleResultsPage"] ||
		store.getters["navigationChecks/isScrabbleExactMatchResultsPage"]
	)
		return "/scrabble-dictionary";
	return "/unscramble";
};

/**
 * Given a page from words lists, it returns the type of word lists page it is.
 * @param {String} routeName - A $route.name string
 * @return {String} the type of words list page corresponding to the routeName
 */
export const getWordListMode = (routeName) => {
	switch (routeName) {
		case PAGES.WORDS_HAVING_INDEX:
		case PAGES.WORDS_HAVING_RESULTS:
			return WORDS.HAVING;
			break;
		case PAGES.WORDS_ENDING_INDEX:
		case PAGES.WORDS_ENDING_RESULTS:
		case PAGES.WORDS_ENDING_ING:
			return WORDS.ENDING;
			break;
		case PAGES.WORDS_STARTING_INDEX:
		case PAGES.WORDS_STARTING_RESULTS:
			return WORDS.STARTING;
			break;
		case PAGES.WORDS_BY_LENGTH_INDEX:
		case PAGES.WORDS_BY_LENGTH_RESULTS:
			return WORDS.LENGTH;
			break;
		case PAGES.WORDS_CONSONANTS:
		case PAGES.WORDS_ENDING_ING:
		case PAGES.WORDS_WITH_Q_NO_U:
		case PAGES.WORDS_VOWELS:
			return WORDS.GENERAL;
			break;

		default:
			return false;
	}
};

export const resetStoreForWordListQuery = (route, store) => {
	let urlParameter = "";

	if (getWordListMode(route.name)) {
		store.commit("clearAdvancedSearch");
		store.commit("clearAllTiles");
	}

	if (store.getters["navigationChecks/isWordsWithTheLetterResultsPage"]) {
		urlParameter = route.params.letter;
		if (
			(urlParameter.includes("with") ||
				urlParameter.includes("starts")) &&
			!urlParameter.includes("-and-")
		) {
			store.commit(
				"updateStartsWith",
				getLettersQueryParam(urlParameter, WORDS.STARTING)
			);
			store.commit(
				"updateEndsWith",
				getLettersQueryParam(urlParameter, WORDS.ENDING)
			);
			urlParameter = getLettersQueryParam(urlParameter, "with");
		}
		if (isEmpty(urlParameter)) {
			urlParameter = route.params.letter;
		}
		urlParameter = urlParameter.replace("-and-", "");
		store.commit("updateContains", urlParameter);
	}

	if (store.getters["navigationChecks/isWordsThatStartsWithResultsPage"]) {
		urlParameter = route.params.letter;
		store.commit("updateStartsWith", urlParameter);
	}

	if (store.getters["navigationChecks/isWordsThatEndsInResultsPage"]) {
		urlParameter = route.params.letter;
		store.commit("updateEndsWith", urlParameter);
	}

	if (store.getters["navigationChecks/isWordsEndingInINGResultsPage"]) {
		store.commit("updateEndsWith", "ing");
	}

	if (store.getters["navigationChecks/isWordsByLengthResultsPage"]) {
		urlParameter = route.params.length;
		const isHasLetters = /[a-zA-Z_]+$/.test(urlParameter);
		if (isHasLetters) {
			store.commit(
				"updateStartsWith",
				getLettersQueryParam(urlParameter, WORDS.STARTING)
			);
			store.commit(
				"updateEndsWith",
				getLettersQueryParam(urlParameter, WORDS.ENDING)
			);
			store.commit(
				"updateContains",
				getLettersQueryParam(urlParameter, "with")
			);
		}
		store.commit("updateWordlength", urlParameter.replace(/\D+/g, ""));
	}
};

export const getLettersQueryParam = (letters, prefixString) => {
	if (typeof letters !== "string") return "";
	const string = letters.split(`${prefixString}-`)[1] || "";
	return string.indexOf("-") !== -1
		? string.substring(0, string.indexOf("-"))
		: string;
};

export const hasEndingSlashInPath = (path) => {
	return !isEmpty(path) && path.substr(-1) === "/";
};

/**
 * Takes a route name string and returns the page template
 * string value. Used for content grouping in Google Analytics.
 * @param {string} routeName The name of the route
 * @returns {string} The page template string value.
 */
export const getPageTemplateValue = (routeName) => {
	switch (routeName) {
		case PAGES.HOME:
			return "home";
		case PAGES.APP_ABOUT:
			return "about-app";
		case PAGES.APP_PRIVACY_POLICY:
			return "legal-info";
		case PAGES.FOURPICS:
			return "fourpics";
		case PAGES.SCRABBLE_DICT_RESULTS:
		case PAGES.UNSCRAMBLER_RESULTS:
		case PAGES.WORDS_BY_LENGTH_RESULTS:
		case PAGES.WORDS_STARTING_RESULTS:
		case PAGES.WORDS_ENDING_RESULTS:
		case PAGES.WORDS_HAVING_RESULTS:
		case PAGES.WORDS_VOWELS:
		case PAGES.WORDS_CONSONANTS:
		case PAGES.WORDS_WITH_Q_NO_U:
		case PAGES.WORDS_ENDING_ING:
		case PAGES.WORDLE_RESULTS:
			return "results";
		case PAGES.WORDS_LISTS_INDEX:
			return "word-lists-index";
		case PAGES.WORDS_BY_LENGTH_INDEX:
		case PAGES.WORDS_STARTING_INDEX:
		case PAGES.WORDS_ENDING_INDEX:
		case PAGES.WORDS_HAVING_INDEX:
			return "words-lists";
		case PAGES.GAME_CHEATS:
			return "game-cheats";
		case PAGES.ANAGRAM:
		case PAGES.WORD_DESCRAMBLER:
		case PAGES.WORDBRAIN_ANSWERS:
		case PAGES.TEXT_TWIST:
		case PAGES.SCRABBLE_CHECKER:
		case PAGES.JUMBLE_SOLVER:
		case PAGES.SCRABBLE_DICT:
		case PAGES.WORD_CHUMS_CHEAT:
		case PAGES.UNSCRAMBLE:
		case PAGES.WORDCOOKIES:
		case PAGES.WORDFEUD:
		case PAGES.WORD_MAKER:
		case PAGES.WWF:
		case PAGES.LETTERS_AND_BLANK:
		case PAGES.WORD_SWIPE_CHEATS:
		case PAGES.WORD_CONNECT:
		case PAGES.CODY_CROSS:
		case PAGES.WORD_STORY:
		case PAGES.WORDLE:
			return "game-landing";
		case PAGES.ARTICLE_INDEX:
			return "articles-index";
		case PAGES.ARTICLE_DETAIL:
			return "articles-detail";
		case PAGES.SAVED_WORDS:
			return "saved-words";
		case PAGES.NOT_FOUND:
			return "page-not-found";
		default:
			return "uncategorised";
	}
};

/**
 * Checks if the given route represent a combination page:
 * a page where two or more advanced
 * search fields are present in the URL, e.g.
 * "/letter-words/5-with-s/" or
 * "/words-with-the-letter/start-z-ends-y".
 *
 * One single field having more than one character
 * (e.g. "/word-with-the-letter/a-and-c/" or
 * "/words-that-start/ab/") do not count.
 * @param route the VUE route
 * @returns {boolean} true if the path belong to a combined wordlist page. false otherwise.
 */
export const isCombinationPage = (route) => {
	const isLetterWordsCombinedPage =
		!isEmpty(route.params.length) &&
		/[a-zA-Z_]+$/.test(route.params.length);
	const isWordsWithLettersCombinedPage =
		route.name === PAGES.WORDS_HAVING_RESULTS &&
		/(starts|with|ends)\-/.test(route.params.letter);
	return isLetterWordsCombinedPage || isWordsWithLettersCombinedPage;
};

export const getWlCombinationParamsAsObject = (paramString) => {
	const response = {
		starts: "",
		with: "",
		ends: "",
		length: "",
	};

	response.starts = getLettersQueryParam(paramString, "starts").toUpperCase();
	response.ends = getLettersQueryParam(paramString, "ends").toUpperCase();
	response.with = getLettersQueryParam(paramString, "with").toUpperCase();

	response.length = paramString.replace(/\D/g, "") ?? "";

	return response;
};

export const buildWordListPathFromFields = (
	length = "",
	starts = "",
	contains = "",
	ends = ""
) => {
	// If a number is supplied, convert it to a string. Because we're nice.
	if (typeof length === "number") length = String(length);

	// Default everything to an empty string if not a valid string value.
	[length, starts, contains, ends] = [
		length,
		starts,
		contains,
		ends,
	].map((param) =>
		typeof param === "string" && !isEmpty(param) ? param : ""
	);

	const paramCheck = {
		starts,
		ends,
		with: contains,
	};

	// Using an array ensures the order of the keys.
	const paramOrder = ["starts", "with", "ends"];
	const nonEmptyParams = paramOrder.reduce((acc, key) => {
		const value = paramCheck[key];
		if (!isEmpty(value)) {
			acc.push(`${key}-${value}`);
		}

		return acc;
	}, []);

	// All combinations that have the length param are automatically letter-word URLs.
	if (!isEmpty(length)) {
		let segments = [length];

		if (nonEmptyParams.length) {
			segments = [...segments, ...nonEmptyParams];
		}

		return `/letter-words/${segments.join("-")}/`;
	}

	// All remaining combinations that have more than 1 param are automatically words-with-the-letter URLs.
	if (nonEmptyParams.length > 1) {
		return `/words-with-the-letter/${nonEmptyParams.join("-")}/`;
	}

	// From here there can only ever be one param non-empty.
	if (!isEmpty(starts)) {
		return `/words-that-start/${starts}/`;
	}

	if (!isEmpty(ends)) {
		return `/ending-with/${ends}/`;
	}

	if (!isEmpty(contains)) {
		return `/words-with-the-letter/${contains}/`;
	}

	console.warn(
		"Invalid parameter scenario reached for buildWordListPathFromFields",
		arguments
	);
	return "";
};

/**
 *
 * @param {string?} tiles
 * @param {string} game
 * @param {object} queryParams
 * @param {string?} queryParams.starts
 * @param {string?} queryParams.contains
 * @param {string?} queryParams.ends
 * @param {string | number | null} queryParams.length
 * @param {string?} queryParams.correct
 * @param {string?} queryParams.includes
 * @param {string?} queryParams.excludes
 * @returns {string?}
 */
export const getResultsUrl = (
	tiles = "",
	game = "UNSCRAMBLE",
	queryParams = {}
) => {
	tiles = typeof tiles === "string" ? tiles.toLowerCase() : "";

	/**
	 * @todo these should not be repeated strings, turn them into consts.
	 */
	const validGames = [
		"UNSCRAMBLE",
		"SCRABBLE",
		"ANAGRAM",
		"WORDLE",
		"QUORDLE",
	];

	if (typeof game === "string") {
		game = game.toUpperCase();
	}

	if (!validGames.includes(game)) {
		console.warn(
			`"${game}" is an invalid game parameter. Defaulting to "UNSCRAMBLE"`
		);
		game = validGames[0];
	}

	switch (game) {
		case "SCRABBLE": {
			if (tiles.length) {
				return `/scrabble-dictionary/${tiles}/`;
			}
			break;
		}
		case "ANAGRAM": {
			if (tiles.length) {
				return `/anagram-solver/${tiles}/`;
			}
			break;
		}
		case "WORDLE":
		case "QUORDLE": {
			const path =
				game === "WORDLE" ? "/wordle/results/" : "/quordle/results/";

			const params = {};
			if (queryParams.correct?.length) {
				params.correct = queryParams.correct;
			}

			if (queryParams.includes?.length) {
				params.includes = queryParams.includes;
			}

			if (queryParams.excludes?.length) {
				params.excludes = queryParams.excludes;
			}

			if (Object.keys(params).length) {
				const queryString = Object.entries(params)
					.map(([key, value]) => `${key}=${value}`)
					.join("&");
				return `${path}?${queryString}`;
			}

			break;
		}
		case "UNSCRAMBLE": {
			if (!tiles.length) {
				const wordlistUrl = buildWordListPathFromFields(
					queryParams.length?.toString?.(),
					queryParams.starts,
					queryParams.contains,
					queryParams.ends
				);
				if (wordlistUrl) {
					return wordlistUrl;
				}
			} else {
				return `/unscramble/${tiles}/`;
			}
			break;
		}
	}

	return null;
};
